import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import Swal from 'sweetalert2';

import { fetchRoleListApi, deleteRoleApi, getAppliedRoleListForDrCr } from '../../services/api';
import MetaTags from '../../components/MetaTags'; 
import DataTable from '../../components/DataTable'; 
import RolesKpiGrid from '../../components/rolesPage/RolesKpiGrid';
import RolesMobileCard from '../../components/rolesPage/RolesMobileCard';
import { getRolesColumns } from '../../components/rolesPage/RolesColumns';
import './ManageRolesPage.css';
import { useAuth } from '../../context/AuthContext';

const getAuditionLocation = (role, parsedRequestData = {}) => {
  try {
    let locations = role.audition_locations_json || parsedRequestData.audition_locations_json || parsedRequestData.auditionLocations || [];
    if (typeof locations === 'string') locations = JSON.parse(locations);
    if (!Array.isArray(locations) || locations.length === 0) return role.shoot_location || role.roleLocation || 'N/A';

    const formattedLocations = locations.map((location) => {
      const district = location.district || '';
      const state = location.state || '';
      const pincode = location.pincode || '';
      let locationName = district && state ? `${district}, ${state}` : (district || state || location.address1 || '');
      return locationName && pincode ? `${locationName} - ${pincode}` : (locationName || pincode);
    }).filter(Boolean);

    return formattedLocations.length > 0 ? formattedLocations.join('; ') : role.shoot_location || role.roleLocation || 'N/A';
  } catch (error) {
    return role.shoot_location || role.roleLocation || 'N/A';
  }
};

const ManageRolesPage = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const hasPermission = useCallback((moduleKey, action = 'View') => {
    if (String(user?.roleCode || user?.role_code) === '900') {
      if (action === 'Delete') return false;
      return (user?.permissions || []).includes(`${moduleKey}:${action}`);
    }
    return true;
  }, [user]);

  const [dashboardStats, setDashboardStats] = useState({
    totalProjects: 0, totalRoles: 0, totalAuditions: 0, selected: 0, rejected: 0, pending: 0
  });

  const loadRoles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchRoleListApi();
      const rolesData = res?.data?.data || res?.data || [];
      const appResponse = await getAppliedRoleListForDrCr();
      const applications = appResponse?.data?.data || [];

      const mappedRoles = rolesData.map((role) => {
        let parsedRequestData = {};
        if (role.request_data) {
          try { parsedRequestData = typeof role.request_data === 'string' ? JSON.parse(role.request_data) : role.request_data; } catch (e) {}
        }
        const roleApplications = applications.filter(app => String(app.c_roleId || app.c_role_id || app.role_joget_id || app.joget_role_id || app.roleId || '') === String(role.joget_role_id || role.roleId || ''));
        const selectedCount = roleApplications.filter(app => String(app.result || app.c_result || '').toLowerCase() === 'selected').length;
        const rejectedCount = roleApplications.filter(app => String(app.result || app.c_result || '').toLowerCase() === 'rejected').length;
        const pendingCount = roleApplications.filter(app => ['pending', 'resubmit'].includes(String(app.result || app.c_result || '').toLowerCase())).length;

        return {
          ...parsedRequestData,
          id: role.id, joget_role_id: role.joget_role_id,
          roleld: role.roleld || parsedRequestData.roleld || '---',
          roleName: role.role_name || role.roleName || parsedRequestData.roleName || 'N/A',
          roleCategory: role.role_category || role.roleCategory || parsedRequestData.roleCategory || 'N/A',
          auditionType: parsedRequestData.auditionType || role.audition_type || role.auditionType || 'N/A',
          roleLocation: getAuditionLocation(role, parsedRequestData),
          projectName: role.projectName || role['project.projectName'] || role.project?.project_name || parsedRequestData.projectName || '---',
          totalApplicants: roleApplications.length, selectedCount, rejectedCount, pendingCount,
          status: role.status || 'N/A', dateCreated: role.created_at || role.dateCreated || 0,
        };
      });

      const sortedRoles = mappedRoles.sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));
      setRoles(sortedRoles);

      setDashboardStats({
        totalProjects: new Set(sortedRoles.map(r => r.projectName)).size,
        totalRoles: sortedRoles.length,
        totalAuditions: applications.length,
        selected: applications.filter(item => String(item.result || '').toLowerCase() === 'selected').length,
        rejected: applications.filter(item => String(item.result || '').toLowerCase() === 'rejected').length,
        pending: applications.filter(item => ['pending', 'applied', 'resubmit'].includes(String(item.result || '').toLowerCase())).length
      });
    } catch (err) { setError('Failed to load roles.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  const handleEdit = useCallback((localId) => navigate(`/dashboard/post-roles/${localId}`), [navigate]);

  const handleClone = useCallback((role) => {
    const clonedRole = { ...role };
    delete clonedRole.id; delete clonedRole.joget_role_id;
    clonedRole.roleName = `${role.roleName} (Clone)`;
    try {
      sessionStorage.setItem('roleToClone', JSON.stringify(clonedRole));
    } catch (e) {}
    navigate('/dashboard/post-roles');
  }, [navigate]);

  const handleDelete = useCallback(async (roleOrId) => {
    const role = typeof roleOrId === 'object' ? roleOrId : roles.find(r => String(r.id) === String(roleOrId));
    if (!role?.id) return;
    if ((role.totalApplicants || 0) > 0) {
      return Swal.fire({ icon: 'warning', title: 'Cannot Delete Role!', html: `This role currently has <b style="color: #38bdf8">${role.totalApplicants} Applicant(s)</b>.`, background: '#0E1124', color: '#fff', confirmButtonColor: '#d90429', width: '380px' });
    }

    const result = await Swal.fire({ title: 'Are you sure?', text: `Delete role "${role.roleName || ''}"?`, icon: 'warning', showCancelButton: true, background: '#0E1124', color: '#fff', confirmButtonColor: '#d90429', cancelButtonColor: '#1E2342', confirmButtonText: 'Yes, Delete it!', width: '380px' });
    if (result.isConfirmed) {
      try {
        await deleteRoleApi(role.id);
        Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Role deleted successfully!', background: '#0E1124', color: '#fff', confirmButtonColor: '#10B981', width: '380px' });
        loadRoles();
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Delete Failed!', text: err.response?.data?.message || 'Failed to delete role.', background: '#0E1124', color: '#fff', confirmButtonColor: '#d90429', width: '380px' });
      }
    }
  }, [roles, loadRoles]);

  const columns = useMemo(() => getRolesColumns({ handleEdit, handleClone, handleDelete, hasPermission }), [handleEdit, handleClone, handleDelete, hasPermission]);
  const renderMobileCard = useCallback((role) => (
    <RolesMobileCard role={role} handleEdit={handleEdit} handleClone={handleClone} handleDelete={handleDelete} hasPermission={hasPermission} />
  ), [handleEdit, handleClone, handleDelete, hasPermission]);

  if (loading) return (
    <div className="text-center p-5">
      <Spinner animation="border" variant="danger" />
      <p className="text-white mt-2">Loading Roles...</p>
    </div>
  );

  return (
    <div className="manage-roles-page-wrapper p-2 p-md-3">
      <MetaTags title="Manage Roles" />
      <div className="page-header d-flex justify-content-between align-items-center mb-3">
        <h5 className="text-uppercase fw-bold text-white m-0">
          Manage <span style={{ color: 'var(--primary-color)' }}>Roles</span>
        </h5>
        {hasPermission('Roles', 'Create') && (
          <Button as={Link} to="/dashboard/post-roles" variant="danger" className="btn-compact-header fw-bold">
            <FaPlus className="me-1" /> Post Role
          </Button>
        )}
      </div>

      {error && <Alert variant="danger" className="bg-dark text-danger border-danger py-2 small">{error}</Alert>}
      <RolesKpiGrid dashboardStats={dashboardStats} />
      <DataTable columns={columns} data={roles} renderMobileItem={renderMobileCard} searchPlaceholder="Search roles or projects..." />
    </div>
  );
};

export default ManageRolesPage;